import { isCompleteRetailScanCode, isPartialInternalScanCode } from './posScanCode.js';

export const UNIFIED_RETURN_PAGE_SIZE = 20;

export const DATE_SCOPE_OPTIONS = [
  { value: 'DEFAULT', label: '12 derniers mois (défaut)' },
  { value: 'ALL', label: 'Toutes les dates' },
  { value: 'RANGE', label: 'Période personnalisée' },
];

export const CANAL_FILTER_OPTIONS = [
  { value: '', label: 'Tous les canaux' },
  { value: 'POS', label: 'Comptoir' },
  { value: 'CLICK_AND_COLLECT', label: 'Click & Collect' },
  { value: 'LIVRAISON', label: 'Livraison' },
  { value: 'WEB', label: 'Site' },
];

export const RETURN_STATUS_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'RETURNABLE', label: 'Solde remboursable' },
  { value: 'PARTIALLY_REFUNDED', label: 'Partiellement remboursé' },
  { value: 'FULLY_REFUNDED', label: 'Totalement remboursé' },
];

/** @typedef {{ q: string, kind: string, page: number, pageSize: number, dateScope: string, dateMin: string, dateMax: string, canalVente: string, statutPaiement: string, returnStatus: string }} UnifiedSearchFormState */

export function hasUnifiedFilterListDiscriminant(form) {
  return Boolean(
    String(form?.canalVente || '').trim() ||
      String(form?.statutPaiement || '').trim() ||
      String(form?.returnStatus || '').trim()
  );
}

export function defaultUnifiedSearchFormState() {
  return {
    q: '',
    kind: 'AUTO',
    page: 1,
    pageSize: UNIFIED_RETURN_PAGE_SIZE,
    dateScope: 'DEFAULT',
    dateMin: '',
    dateMax: '',
    canalVente: '',
    statutPaiement: '',
    returnStatus: '',
  };
}

/**
 * Construit les query params pour GET /caisse/retours-unifies/search.
 * @returns {{ ok: true, params: Record<string, string> } | { ok: false, reason: string, clientMessage?: string }}
 */
export function buildUnifiedReturnSearchParams(form, { preferBarcode = false } = {}) {
  const qTrim = String(form.q ?? '').trim();
  const kind = String(form.kind || 'AUTO').toUpperCase();

  if (kind === 'AUTO' && preferBarcode && qTrim && isCompleteRetailScanCode(qTrim)) {
    return {
      ok: true,
      params: packParams({ ...form, q: qTrim, kind: 'AUTO' }),
    };
  }

  if (kind === 'AUTO' && qTrim && isPartialInternalScanCode(qTrim)) {
    return { ok: false, reason: 'barcode_incomplete', clientMessage: 'Code produit incomplet — continuez la saisie ou scannez.' };
  }

  if (!qTrim && kind === 'AUTO') {
    if (!hasUnifiedFilterListDiscriminant(form)) {
      return {
        ok: false,
        reason: 'query_empty',
        clientMessage: 'Saisissez une référence, scannez un code, ou choisissez un filtre (canal, statut).',
      };
    }
    if (form.dateScope === 'ALL') {
      return {
        ok: false,
        reason: 'filter_unbounded',
        clientMessage:
          'Sans texte de recherche, utilisez « 12 derniers mois » ou une période personnalisée.',
      };
    }
    return { ok: true, params: packParams({ ...form, q: '', kind: 'AUTO' }) };
  }

  if (kind === 'AUTO' && form.dateScope === 'ALL' && qTrim.length > 0 && qTrim.length < 3) {
    return {
      ok: false,
      reason: 'query_too_short_all_dates',
      clientMessage: 'Sur « Toutes les dates », saisissez au moins 3 caractères.',
    };
  }

  if (form.dateScope === 'RANGE' && !String(form.dateMin || '').trim()) {
    return { ok: false, reason: 'date_min_required', clientMessage: 'Indiquez une date de début pour la période.' };
  }

  return { ok: true, params: packParams({ ...form, q: qTrim, kind }) };
}

function packParams(form) {
  const params = {
    kind: form.kind || 'AUTO',
    page: String(Math.max(1, Number(form.page) || 1)),
    pageSize: String(form.pageSize || UNIFIED_RETURN_PAGE_SIZE),
    dateScope: form.dateScope || 'DEFAULT',
  };
  const qPacked = String(form.q ?? '').trim();
  if (qPacked) params.q = qPacked;
  if (form.dateScope === 'RANGE') {
    if (form.dateMin) params.dateMin = form.dateMin;
    if (form.dateMax) params.dateMax = form.dateMax;
  }
  if (form.canalVente) params.canalVente = form.canalVente;
  if (form.statutPaiement) params.statutPaiement = form.statutPaiement;
  if (form.returnStatus) params.returnStatus = form.returnStatus;
  return params;
}

export function unifiedSearchParamsFingerprint(params) {
  return JSON.stringify(params);
}

export function isUnifiedSearchResponseStillValid(fingerprintAtRequest, currentParams) {
  return fingerprintAtRequest === unifiedSearchParamsFingerprint(currentParams);
}

export function isCurrentSearchGeneration(requestSeq, latestSeq) {
  return requestSeq === latestSeq;
}

export function unifiedReturnSearchHintMessage(code, hints = []) {
  const map = {
    QUERY_TOO_SHORT: 'Saisissez au moins 2 caractères (3 sur toutes les dates).',
    BARCODE_INCOMPLETE: 'Code produit incomplet.',
    BARCODE_INVALID: 'Code-barres invalide.',
    BARCODE_NOT_FOUND: 'Aucune vente pour ce code produit.',
    SKU_NOT_FOUND: 'Référence SKU introuvable.',
    AMOUNT_NEEDS_DATE_RANGE: 'Pour un montant, choisissez une période personnalisée.',
    DATE_MIN_REQUIRED: 'Date de début requise.',
    DATE_RANGE_INVALID: 'Plage de dates invalide.',
    DATE_RANGE_TOO_WIDE: 'Plage trop large (max. 730 jours).',
    KIND_INVALID: 'Type de recherche invalide.',
    PHONE_INVALID: 'Numéro de téléphone invalide.',
    AMOUNT_INVALID: 'Montant invalide.',
  };
  if (code && map[code]) return map[code];
  const hint = hints?.[0];
  if (hint && map[hint]) return map[hint];
  return null;
}

export function resolveUnifiedReturnBackNavigation({ step, detailOpen }) {
  if (detailOpen || step === 'detail') return 'back_to_search';
  return 'exit_to_caisse';
}

export function mapRefundRouteLabel(route) {
  switch (route) {
    case 'POS_TICKET':
      return 'Retour produits (ticket POS comptoir)';
    case 'WEB_ENCAISSE_CAISSE':
      return 'Remboursement caisse (commande encaissée magasin)';
    case 'STRIPE_WEB':
      return 'Remboursement Stripe (Mes Commandes)';
    case 'BLOCKED_MIXED_TENDERS':
      return 'Paiement mixte — remboursement sécurisé indisponible';
    case 'BLOCKED_NO_CAISSE_PROOF':
      return 'Preuve d’encaissement insuffisante';
    default:
      return route ? String(route) : '—';
  }
}

export function formatUnifiedClientLabel(client) {
  if (!client) return null;
  const label = client.label != null ? String(client.label).trim() : '';
  if (label && label !== 'Client') return label;
  if (client.displayName) return client.displayName;
  const parts = [client.prenom, client.nom].filter(Boolean);
  if (parts.length) return parts.join(' ');
  if (client.emailMasked) return client.emailMasked;
  if (client.telephoneMasked) return client.telephoneMasked;
  if (label === 'Client') return null;
  return null;
}

/** Affichage fiche détail — section client toujours visible (U1-FIX). */
export function resolveUnifiedClientSection(client) {
  const displayLabel = formatUnifiedClientLabel(client);
  const isPlaceholder =
    !displayLabel || client?.identityStatus === 'NOT_PROVIDED';
  return {
    displayLabel: isPlaceholder ? 'Client non renseigné' : displayLabel,
    isPlaceholder,
  };
}
